import React, { useRef } from 'react';
import PlacesAutocomplete, { PropTypes as PlacesAutocompleteProps } from 'react-places-autocomplete';

import {
  CircularProgress,
  createStyles,
  Grow,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  WithStyles,
  withStyles,
} from '@material-ui/core';
import { PopperProps } from '@material-ui/core/Popper';
import classnames from 'classnames';
import { find, isEmpty, map } from 'lodash';
import { compose } from 'redux';

import { usePlacesService } from '@lib/hooks';

import { TextField, TextFieldProps } from 'components/v2';

const styles = createStyles({
  textField: {
    minHeight: 'initial',
    width: '100%',
  },
  list: {
    padding: 0,
  },
});

const ADDRESS_FIELDS = {
  COMPONENTS: 'address_components',
  COUNTRY: 'country',
  STATE: 'administrative_area_level_1',
  ZIP_CODE: 'postal_code',
  CITY: 'locality',
  AREA: 'administrative_area_level_2',
  ROUTE: 'route',
  STREET_NUMBER: 'street_number',
};

const searchOptions = {
  componentRestrictions: {
    country: 'us',
  },
  types: ['geocode'],
};

type Place = Record<string, any>;

export type TextFieldAddressAutocompleteValues = {
  country: string;
  state: string;
  postcode: string;
  city: string;
  address: string;
  value: string;
};

export interface TextFieldAddressAutocompleteProps
  extends WithStyles<typeof styles>,
    Omit<TextFieldProps, 'classes' | 'value' | 'onChange' | 'onSelect'> {
  value: string;
  onChange: PlacesAutocompleteProps['onChange'];
  onSelect: (place: Place, values: TextFieldAddressAutocompleteValues) => void;
  popperProps?: Partial<PopperProps> & { className?: string };
}

const TextFieldAddressAutocomplete: React.FC<TextFieldAddressAutocompleteProps> = ({
  classes,
  className,
  value,
  onChange,
  onSelect,
  popperProps,
  ...rest
}) => {
  const [service] = usePlacesService('TextFieldAddressAutocomplete');
  const inputRef = useRef<any>();

  const getAddressData = (place: Place, type: string) => {
    const res = find(place[ADDRESS_FIELDS.COMPONENTS], (data) => data.types[0] === type);
    return res ? res.long_name : null;
  };

  const handleSelect = (address: string, placeId: string) => {
    // From 'react-places-autocomplete' docs about its onSelect - NOTE: `placeId` and `suggestion`
    // are null when user hits Enter key with no suggestion item selected.
    if (placeId) {
      new (window as any).google.maps.places.PlacesService(document.createElement('div')).getDetails(
        /*
         If no fields are specified, all available fields will be returned and billed for.
         https://developers.google.com/maps/documentation/javascript/reference/places-service#PlaceDetailsRequest.fields
        */
        { placeId, fields: ['formatted_address', 'address_components'] },
        (place: Place, status: number) => {
          if (status === (window as any).google.maps.places.PlacesServiceStatus.OK) {
            const newValue = place.formatted_address;
            const street = getAddressData(place, ADDRESS_FIELDS.ROUTE);
            const streetNumber = getAddressData(place, ADDRESS_FIELDS.STREET_NUMBER);
            const values = {
              country: getAddressData(place, ADDRESS_FIELDS.COUNTRY),
              state: getAddressData(place, ADDRESS_FIELDS.STATE),
              postcode: getAddressData(place, ADDRESS_FIELDS.ZIP_CODE),
              city: getAddressData(place, ADDRESS_FIELDS.CITY) || getAddressData(place, ADDRESS_FIELDS.AREA),
              address: streetNumber ? `${streetNumber} ${street}` : street,
              value: newValue,
            };
            onSelect(place, values);
          }
        },
      );
    }
  };

  if (!service) {
    return null;
  }
  return (
    <PlacesAutocomplete
      searchOptions={searchOptions}
      value={value}
      onChange={onChange}
      onSelect={handleSelect}
      onError={() => {}}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <>
          <TextField
            inputRef={inputRef}
            {...getInputProps({
              className: classnames(classes.textField, className),
              autoComplete: 'disabled',
              InputProps: loading
                ? {
                    id: 'no-auto-fill',
                    endAdornment: (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ),
                  }
                : {
                    id: 'no-auto-fill',
                  },
              ...rest,
            })}
          />
          <Popper
            open={!isEmpty(suggestions)}
            anchorEl={inputRef.current}
            transition
            placement="bottom-start"
            disablePortal={false}
            {...popperProps}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps}>
                <Paper>
                  <List component="nav" className={classes.list}>
                    {map(suggestions, (suggestion) => {
                      const props = getSuggestionItemProps(suggestion, { button: true });
                      return (
                        <ListItem {...props} key={suggestion.placeId}>
                          <ListItemText primary={suggestion.description} />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              </Grow>
            )}
          </Popper>
        </>
      )}
    </PlacesAutocomplete>
  );
};

export default compose(withStyles(styles, { name: 'TextFieldAddressAutocomplete' }))(TextFieldAddressAutocomplete);
